import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import InlineTextButton from '@ben/orion/inline-text-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ITheme } from '@ben/orion/utils/theme';
import type { IModularBenefitDetail, IStep } from '@ben/shared/types/columbia';
import type {
  DraggingStyle,
  DropResult,
  NotDraggingStyle,
} from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { last } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styled, { css, useTheme } from 'styled-components';
import { useSessionStorage } from 'usehooks-ts';
import { generateNewRank } from '../../utils';
import { useSetDirtyValue } from '../hooks/use-set-dirty-value';
import ReadOnlyBanner from '../read-only-banner';
import { sanitizeStepsForSave } from '../services/sanitize-steps';
import Step from './step';
import { toggleAccordion } from './toggleAccordion';

interface ModularStepsProps {
  isScheduledVersion: boolean;
}

const ModularSteps = ({ isScheduledVersion }: ModularStepsProps) => {
  const { control, getValues } = useFormContext<IModularBenefitDetail>();
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();
  const theme = useTheme();

  const {
    fields: initialFields,
    append,
    replace,
    swap,
  } = useFieldArray({
    name: 'additionalConfig.steps',
    control,
    keyName: '_id',
  });
  const watched = useWatch({
    name: 'additionalConfig.steps',
    exact: true,
    defaultValue: initialFields,
  }) as IStep[] | undefined;
  const fields = watched ?? initialFields;
  const hasSteps = fields.length > 0;

  const [openSteps, setOpenSteps] = useSessionStorage<number[]>(
    'openSteps',
    []
  );
  const [isDragEnabled, setIsDragEnabled] = useSessionStorage(
    'isDragEnabled',
    false
  );

  const handleStepToggle = (stepRank: number) => {
    toggleAccordion(setOpenSteps, stepRank);
  };

  useEffect(() => {
    const lastField = last(fields);
    if (
      lastField &&
      Array.isArray(lastField.components) &&
      lastField.components.length > 0 &&
      lastField.components.every((component) => component.importedFromAirtable)
    ) {
      setOpenSteps([lastField.rank]);
    }
  }, [fields, setOpenSteps]);

  const handleExpandOrCollapseAll = useCallback(() => {
    if (openSteps.length > 0) {
      setOpenSteps([]);
    } else {
      setOpenSteps(fields.map((step: IStep) => step.rank));
    }
  }, [openSteps.length, fields, setOpenSteps]);

  const onDragEnd = (result: {
    destination?: { index: number };
    source: { index: number };
  }) => {
    if (!result.destination) {
      return;
    }
    const [sourceIndex, destinationIndex] = [
      result.source.index,
      result.destination.index,
    ];
    swap(sourceIndex, destinationIndex);

    const reorderedSteps: IStep[] = [...fields];
    const [removed] = reorderedSteps.splice(sourceIndex, 1);
    if (removed) {
      reorderedSteps.splice(destinationIndex, 0, removed);
    }

    const rankedReorderedSteps = reorderedSteps.map(
      (step: IStep, index: number) => ({
        ...step,
        rank: index,
      })
    );

    setDirtyValue('additionalConfig.steps', rankedReorderedSteps);
  };

  const handleSwitchDragEnabled = () => {
    setIsDragEnabled(!isDragEnabled);
    setOpenSteps([]);
  };

  const handleRemoveStep = (indexToRemove: number) => {
    const remainingSteps = sanitizeStepsForSave(
      (getValues('additionalConfig.steps') ?? []).filter(
        (_, index) => index !== indexToRemove
      )
    );

    replace(remainingSteps);
    setDirtyValue('additionalConfig.steps', remainingSteps);
    setOpenSteps((previousOpenSteps) =>
      previousOpenSteps
        .filter((openIndex) => openIndex !== indexToRemove)
        .map((openIndex) =>
          openIndex > indexToRemove ? openIndex - 1 : openIndex
        )
        .filter((openIndex) => openIndex < remainingSteps.length)
    );
  };

  const addNewStep = () => {
    const newRank = generateNewRank(fields) ?? fields.length;
    const newStep: IStep = {
      id: undefined,
      tempId: `temp-id-${String(Date.now())}`,
      rank: newRank,
      name: '',
      components: [],
    };

    append(newStep);

    const sanitizedSteps = sanitizeStepsForSave(
      getValues('additionalConfig.steps') ?? []
    );
    setDirtyValue('additionalConfig.steps', sanitizedSteps);

    const newStepIndex = sanitizedSteps.findIndex(
      (step) => step.tempId === newStep.tempId
    );
    setOpenSteps((previousOpenSteps) => {
      const validOpenSteps = previousOpenSteps.filter(
        (openIndex) => openIndex < sanitizedSteps.length
      );
      if (newStepIndex < 0) {
        return validOpenSteps;
      }
      return [...validOpenSteps, newStepIndex];
    });
  };

  if (isScheduledVersion) {
    return (
      <Wrapper>
        <StepsHeader>
          <Typography variant={TypographyVariant.h2}>Steps</Typography>
        </StepsHeader>
        <ReadOnlyBanner />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <StepsHeader>
        <Typography variant={TypographyVariant.h2}>Steps</Typography>
        <StepsHeaderButtonsContainer>
          {hasSteps && (
            <>
              <InlineTextButton
                onClick={handleExpandOrCollapseAll}
                label={openSteps.length > 0 ? '- Collapse all' : '+ Expand all'}
                disabled={isDragEnabled}
              />
              <Button
                width="fit-content"
                variant={ButtonVariant.secondary}
                onClick={handleSwitchDragEnabled}
                label={isDragEnabled ? 'Exit Reordering' : 'Reorder Steps'}
              />
            </>
          )}
          {!hasSteps && (
            <Button
              width="fit-content"
              variant={ButtonVariant.primary}
              onClick={addNewStep}
              label="Add Step"
            />
          )}
        </StepsHeaderButtonsContainer>
      </StepsHeader>
      {hasSteps ? (
        <>
          <StyledDragDropContext
            onDragEnd={onDragEnd as (result: DropResult) => void}
          >
            <Droppable droppableId="droppable">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((step: IStep, index: number) => {
                    const stepId = (step as { _id?: string })._id;
                    return (
                      <Draggable
                        key={String(stepId ?? index)}
                        draggableId={String(stepId ?? index)}
                        index={index}
                        isDragDisabled={!isDragEnabled}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            key={String(stepId ?? index)}
                            style={getItemStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                              isDragEnabled,
                              theme
                            )}
                          >
                            <Step
                              key={step.id ?? step.tempId}
                              index={index}
                              onRemove={handleRemoveStep}
                              isExpanded={openSteps.includes(index)}
                              onToggle={handleStepToggle}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                </div>
              )}
            </Droppable>
          </StyledDragDropContext>
          <AddStepButtonWrapper>
            <Button
              width="fit-content"
              variant={ButtonVariant.primary}
              onClick={addNewStep}
              label="Add Step"
              disabled={isDragEnabled}
            />
          </AddStepButtonWrapper>
        </>
      ) : (
        <Typography variant={TypographyVariant.bodyMd1}>
          No steps have been added yet
        </Typography>
      )}
    </Wrapper>
  );
};

export default ModularSteps;

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
  isDragEnabled: boolean,
  theme: ITheme
) => ({
  border: getItemBorder(isDragging, isDragEnabled, theme),
  marginBottom: isDragEnabled ? theme.spacing(1) : 0,
  borderRadius: 5,
  overflow: 'hidden',
  ...draggableStyle,
  zIndex: 1,
});

const getItemBorder = (
  isDragging: boolean,
  isDragEnabled: boolean,
  theme: ITheme
) => {
  if (isDragging) {
    return `2px solid ${theme.palette.primary.light}`;
  }
  if (isDragEnabled) {
    return `1px solid ${theme.palette.primary.bg}`;
  }
  return '1px solid transparent';
};

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    border: 1px solid ${theme.palette.neutral[60]};
    padding: ${theme.spacing(3)};
    border-radius: 10px;

    & > [data-rfd-droppable-id='droppable'] {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(2)};
    }
  `}
`;

const StepsHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StepsHeaderButtonsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;

const StyledDragDropContext = styled(DragDropContext)`
  ${({ theme }) => css`
    width: 100%;
    background: ${theme.palette.primary.bg};
  `}
`;

const AddStepButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
`;
